<script setup>
import { reactive, ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import NaturalLanguageDateTimePicker from 'dashboard/components-next/DateTimePicker/NaturalLanguageDateTimePicker.vue';
// reply-template-picker.TYPES.2 — same media tile as the reply box.
import KaptionTemplateHeaderMedia from 'dashboard/components/widgets/conversation/KaptionReplyTemplate/KaptionTemplateHeaderMedia.vue';
// Canonical, shared WhatsApp preview (wapreview.kaptionai.com iframe) used across
// the app. `bare` renders message-only (no phone chrome).
import KaptionWhatsAppPreviewEnhanced from 'dashboard/components-next/Campaigns/KaptionWhatsAppPreviewEnhanced.vue';
import {
  parseBodyVariables,
  buildInitialBodyValues,
  buildInitialHeaderMedia,
  buildInitialHeaderValues,
  COPY_CODE_MAX_LENGTH,
  hasMediaHeader,
  getButtonsNeedingParam,
  isComposeValid,
  parseHeaderVariables,
  renderTemplateBody,
  renderTemplateHeader,
} from 'dashboard/components-next/NewConversationModal/helpers/newConversationHelpers';
// NOTICE: KAPTION-INTEGRATION - whatsapp-flows: FLOW button prefill / status
import KaptionFlowTemplateSection from 'dashboard/components-next/Campaigns/KaptionFlowTemplateSection.vue';
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
import {
  WHATSAPP_FLOWS_FLAG,
  templateHasFlowButton,
} from 'dashboard/helper/kaptionWhatsappFlows';

const props = defineProps({
  template: { type: Object, required: true },
  contact: { type: Object, default: null },
  inboxName: { type: String, default: 'Business' },
  isSending: { type: Boolean, default: false },
  // NOTICE: KAPTION-INTEGRATION - whatsapp-flows: flow library lookup
  inboxId: { type: [Number, String], default: null },
});

const emit = defineEmits(['back', 'cancelTemplate', 'send', 'change']);

const { t } = useI18n();

const bodyVars = computed(() => parseBodyVariables(props.template));
const dynamicButtons = computed(() => getButtonsNeedingParam(props.template));
const isPendingTemplate = computed(
  () => String(props.template.status || '').toUpperCase() !== 'APPROVED'
);

const bodyValues = reactive(
  buildInitialBodyValues(props.template, { contact: props.contact })
);
const headerMedia = reactive(buildInitialHeaderMedia(props.template));
const headerVars = computed(() => parseHeaderVariables(props.template));
const headerValues = reactive(buildInitialHeaderValues(props.template));
const buttonValues = reactive({});

// NOTICE: KAPTION-INTEGRATION - whatsapp-flows: the flow section writes
// buttonValues.flow, which the helpers turn into the FLOW button entry.
const { isKaptionFlagEnabled } = useKaptionFeatureFlags();
const showFlowSection = computed(
  () =>
    isKaptionFlagEnabled(WHATSAPP_FLOWS_FLAG) &&
    templateHasFlowButton(props.template)
);
const updateFlowBinding = value => {
  buttonValues.flow = value;
};

const isMediaTemplate = computed(() => hasMediaHeader(props.template));

// A preview clone whose BODY text already has the filled values substituted and
// whose media header carries the uploaded URL. Passed to the shared iframe
// preview so it live-updates as the user types (independent of the iframe's
// variable-binding contract).
const previewTemplate = computed(() => {
  const clone = JSON.parse(JSON.stringify(props.template));
  const body = clone.components?.find(c => c.type === 'BODY');
  if (body) {
    body.text = renderTemplateBody(props.template, bodyValues);
  }
  const textHeader = clone.components?.find(
    c => c.type === 'HEADER' && c.format === 'TEXT'
  );
  if (textHeader) {
    textHeader.text = renderTemplateHeader(props.template, headerValues);
  }
  if (isMediaTemplate.value && headerMedia.media_url) {
    const header = clone.components?.find(c => c.type === 'HEADER');
    if (header) {
      header.example = {
        header_handle: [headerMedia.media_url],
        header_url: [headerMedia.media_url],
      };
    }
  }
  return clone;
});

// --- Scheduling ---
const sendMode = ref('now'); // 'now' | 'schedule'
const scheduledAt = ref(null); // ISO string
const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

const isScheduleValid = computed(() => {
  if (sendMode.value !== 'schedule') return true;
  if (!scheduledAt.value) return false;
  const when = new Date(scheduledAt.value);
  return !Number.isNaN(when.getTime()) && when.getTime() > Date.now();
});

const detailsValid = computed(() =>
  isComposeValid(
    props.template,
    bodyValues,
    headerMedia,
    buttonValues,
    headerValues
  )
);

const isValid = computed(() => detailsValid.value && isScheduleValid.value);

const emitChange = () => {
  emit('change', {
    bodyValues: { ...bodyValues },
    headerMedia: { ...headerMedia },
    headerValues: { ...headerValues },
    buttonValues: { ...buttonValues },
    isValid: isValid.value,
  });
};

watch(
  [bodyValues, headerValues, headerMedia, buttonValues, sendMode, scheduledAt],
  emitChange,
  {
    deep: true,
  }
);

const updateHeaderMedia = value => Object.assign(headerMedia, value);

const submitLabel = computed(() => {
  if (sendMode.value === 'schedule') {
    return t('NEW_CONVERSATION_MODAL.SCHEDULE.SCHEDULE_SEND');
  }
  if (isPendingTemplate.value) {
    return t('NEW_CONVERSATION_MODAL.TEMPLATE.QUEUE');
  }
  return t('NEW_CONVERSATION_MODAL.SEND');
});

const handleSubmit = () => {
  if (!isValid.value || props.isSending) return;
  emit('send', {
    bodyValues: { ...bodyValues },
    headerMedia: { ...headerMedia },
    headerValues: { ...headerValues },
    buttonValues: { ...buttonValues },
    schedule:
      sendMode.value === 'schedule'
        ? { at: scheduledAt.value, timezone }
        : null,
  });
};

onMounted(emitChange);
</script>

<template>
  <div class="grid grid-cols-1 gap-6 md:grid-cols-[minmax(0,1fr)_20rem]">
    <!-- Left: fill-in details + scheduling -->
    <div class="flex flex-col gap-4">
      <div
        v-if="isPendingTemplate"
        class="flex gap-2 p-3 text-xs border rounded-lg border-n-weak bg-n-slate-3 text-n-slate-11"
        data-testid="pending-template-notice"
      >
        <span class="i-lucide-clock-3 mt-0.5 size-4 shrink-0" />
        {{ t('NEW_CONVERSATION_MODAL.TEMPLATE.PENDING_NOTICE') }}
      </div>
      <p class="text-[13px] font-semibold tracking-tight text-n-slate-12">
        {{ t('NEW_CONVERSATION_MODAL.COMPOSE.FILL_DETAILS') }}
      </p>

      <!-- Media header -->
      <div v-if="isMediaTemplate" class="flex flex-col gap-1.5">
        <label class="text-xs font-medium text-n-slate-11">
          {{
            t('NEW_CONVERSATION_MODAL.COMPOSE.MEDIA_HEADER', {
              type: headerMedia.media_type,
            })
          }}
        </label>
        <KaptionTemplateHeaderMedia
          :template="template"
          :model-value="headerMedia"
          data-testid="media-upload"
          @update:model-value="updateHeaderMedia"
        />
      </div>

      <!-- Header variable (reply-template-picker.TYPES.1) -->
      <div
        v-for="{ token, key } in headerVars"
        :key="`header-${key}`"
        class="flex flex-col gap-1.5"
      >
        <label class="text-xs font-medium text-n-slate-11">
          {{
            t('KAPTION_REPLY_TEMPLATE.EDITOR.HEADER_VARIABLE', { name: key })
          }}
        </label>
        <input
          v-model="headerValues[key]"
          type="text"
          maxlength="60"
          :data-testid="`header-var-input-${key}`"
          :placeholder="token"
          class="w-full px-3 py-2 text-sm border rounded-lg outline-none bg-n-alpha-black1 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:border-n-brand"
        />
      </div>

      <!-- Body variables -->
      <div
        v-for="{ token, key } in bodyVars"
        :key="key"
        class="flex flex-col gap-1.5"
      >
        <label class="text-xs font-medium text-n-slate-11">
          {{ t('NEW_CONVERSATION_MODAL.COMPOSE.VARIABLE', { name: key }) }}
        </label>
        <input
          v-model="bodyValues[key]"
          type="text"
          :data-testid="`var-input-${key}`"
          :placeholder="token"
          class="w-full px-3 py-2 text-sm border rounded-lg outline-none bg-n-alpha-black1 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:border-n-brand"
        />
      </div>

      <!-- NOTICE: KAPTION-INTEGRATION - whatsapp-flows -->
      <KaptionFlowTemplateSection
        v-if="showFlowSection"
        :inbox-id="inboxId"
        :template="template"
        :model-value="buttonValues.flow || {}"
        @update:model-value="updateFlowBinding"
      />

      <!-- Dynamic button parameters -->
      <div
        v-for="btn in dynamicButtons"
        :key="`btn-${btn.index}`"
        class="flex flex-col gap-1.5"
      >
        <label class="text-xs font-medium text-n-slate-11">
          {{
            btn.paramKind === 'copy_code'
              ? t('NEW_CONVERSATION_MODAL.COMPOSE.BUTTON_CODE', {
                  name: btn.text,
                })
              : t('NEW_CONVERSATION_MODAL.COMPOSE.BUTTON_URL', {
                  name: btn.text,
                })
          }}
        </label>
        <input
          v-model="buttonValues[btn.index]"
          type="text"
          :maxlength="
            btn.paramKind === 'copy_code' ? COPY_CODE_MAX_LENGTH : undefined
          "
          :placeholder="btn.paramKind === 'url' ? btn.urlPrefix : undefined"
          :data-testid="`btn-input-${btn.index}`"
          class="w-full px-3 py-2 text-sm border rounded-lg outline-none bg-n-alpha-black1 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:border-n-brand"
        />
      </div>

      <div
        v-if="
          !bodyVars.length &&
          !headerVars.length &&
          !isMediaTemplate &&
          !dynamicButtons.length
        "
        class="text-xs text-n-slate-10"
      >
        {{ t('NEW_CONVERSATION_MODAL.COMPOSE.NO_VARIABLES') }}
      </div>

      <!-- Scheduling -->
      <div class="flex flex-col gap-2 pt-1">
        <label class="text-xs font-medium text-n-slate-11">
          {{ t('NEW_CONVERSATION_MODAL.SCHEDULE.WHEN') }}
        </label>
        <!-- Canonical Kaption segmented-toggle pattern (KAPTION_PLATFORM_STYLE):
             high-contrast inverted active pill reads clearly in light + dark. -->
        <div
          class="grid grid-cols-2 gap-0.5 p-0.5 rounded-lg border border-n-weak bg-n-alpha-black2"
          role="tablist"
        >
          <button
            type="button"
            data-testid="send-mode-now"
            class="flex items-center justify-center gap-1.5 py-1.5 text-xs font-medium rounded-md transition-colors"
            :class="
              sendMode === 'now'
                ? 'bg-n-slate-12 text-n-solid-2 shadow-sm'
                : 'text-n-slate-11 hover:bg-n-alpha-black4'
            "
            @click="sendMode = 'now'"
          >
            <span class="i-lucide-send-horizontal size-3.5" />
            {{ t('NEW_CONVERSATION_MODAL.SCHEDULE.SEND_NOW') }}
          </button>
          <button
            type="button"
            data-testid="send-mode-schedule"
            class="flex items-center justify-center gap-1.5 py-1.5 text-xs font-medium rounded-md transition-colors"
            :class="
              sendMode === 'schedule'
                ? 'bg-n-slate-12 text-n-solid-2 shadow-sm'
                : 'text-n-slate-11 hover:bg-n-alpha-black4'
            "
            @click="sendMode = 'schedule'"
          >
            <span class="i-lucide-clock size-3.5" />
            {{ t('NEW_CONVERSATION_MODAL.SCHEDULE.SCHEDULE_LATER') }}
          </button>
        </div>

        <div
          v-if="sendMode === 'schedule'"
          class="flex flex-col gap-1"
          data-testid="schedule-picker"
        >
          <NaturalLanguageDateTimePicker
            v-model="scheduledAt"
            :placeholder="t('NEW_CONVERSATION_MODAL.SCHEDULE.PLACEHOLDER')"
          />
          <p class="text-[11px] text-n-slate-10">
            {{
              t('NEW_CONVERSATION_MODAL.SCHEDULE.TIMEZONE', { tz: timezone })
            }}
          </p>
        </div>
      </div>

      <!-- Validity hint -->
      <div
        class="flex items-center gap-1.5 text-xs"
        :class="isValid ? 'text-n-teal-10' : 'text-n-slate-10'"
      >
        <span
          :class="
            isValid ? 'i-lucide-check-circle-2' : 'i-lucide-circle-dashed'
          "
          class="size-3.5"
        />
        {{
          isValid
            ? t('NEW_CONVERSATION_MODAL.COMPOSE.ALL_FILLED')
            : t('NEW_CONVERSATION_MODAL.COMPOSE.FILL_REQUIRED')
        }}
      </div>
    </div>

    <!-- Right: live WhatsApp preview — the shared, canonical iframe tool in
         `bare` (phone-less, message-only) mode. Same component as the gallery
         hover popover and the campaign builder. -->
    <div class="flex flex-col gap-2">
      <p class="text-xs font-medium text-n-slate-10">
        {{ t('NEW_CONVERSATION_MODAL.COMPOSE.PREVIEW_TITLE') }}
      </p>
      <div
        class="flex-1 overflow-hidden border shadow-inner rounded-xl border-n-weak min-h-[19rem]"
        data-testid="wa-preview"
      >
        <KaptionWhatsAppPreviewEnhanced
          :template="previewTemplate"
          bare
          mode="render"
          :company-name="inboxName"
          class="h-full"
        />
      </div>
      <p class="text-[11px] text-center text-n-slate-9">
        {{ t('NEW_CONVERSATION_MODAL.COMPOSE.PREVIEW_HINT') }}
      </p>
    </div>
  </div>

  <div
    class="flex items-center justify-between pt-5 mt-5 border-t border-n-weak"
  >
    <div class="flex items-center gap-2">
      <Button
        :label="t('NEW_CONVERSATION_MODAL.BACK')"
        icon="i-lucide-arrow-left"
        variant="ghost"
        color="slate"
        size="sm"
        @click="emit('back')"
      />
      <Button
        :label="t('KAPTION_REPLY_TEMPLATE.CHIP.CANCEL')"
        icon="i-lucide-x"
        variant="ghost"
        color="slate"
        size="sm"
        data-testid="cancel-selected-template"
        @click="emit('cancelTemplate')"
      />
    </div>
    <Button
      :label="submitLabel"
      trailing-icon
      :icon="sendMode === 'schedule' ? 'i-lucide-clock' : 'i-lucide-send'"
      size="sm"
      data-testid="send-button"
      :disabled="!isValid || isSending"
      :is-loading="isSending"
      @click="handleSubmit"
    />
  </div>
</template>
